import * as yup from 'yup';
import { IDinamikFormConfig, IFormAlanConfig } from '../types/form-inputs/IDinamikFormConfig';

/**
 * Mevcut Yup schema'yi DB konfigurasyonuna gore modifiye eder.
 * config null ise orijinal schema'yi degistirmeden doner (graceful fallback).
 */
export function applyConfigToSchema(
  baseSchema: yup.ObjectSchema<any>,
  config: IDinamikFormConfig | null
): yup.ObjectSchema<any> {
  if (!config || !config.alanlar || config.alanlar.length === 0) {
    return baseSchema;
  }

  const overrides: Record<string, yup.AnySchema> = {};

  // Base schema'daki field tiplerini bir kez cikar
  let fieldDescriptions: Record<string, any> = {};
  try {
    const desc = baseSchema.describe();
    fieldDescriptions = (desc.fields || {}) as Record<string, any>;
  } catch {
    // ignore
  }

  for (const alan of config.alanlar) {
    const fieldName = alan.alanKodu;

    // Gizli alanlar icin validasyonu tamamen kaldir
    if (alan.gorunurMu === 'H') {
      overrides[fieldName] = yup.mixed().optional().nullable().default(undefined);
      continue;
    }

    // Zorunluluk 'H' ise — base schema'daki required'i kaldirmamiz gerekiyor
    if (alan.zorunluMu === 'H') {
      // Base schema'daki tipi koru ama required'i kaldir
      const baseType = fieldDescriptions[fieldName]?.type;
      overrides[fieldName] = createNonRequiredField(baseType, alan);
      continue;
    }

    // Zorunluluk 'E' ise — base schema'da required olmayabilir, ekle
    if (alan.zorunluMu === 'E') {
      const baseType = fieldDescriptions[fieldName]?.type;
      const hataMesaji = alan.zorunluHataMesaji || 'Bu alan zorunludur';
      overrides[fieldName] = createRequiredField(baseType, alan, hataMesaji);
      continue;
    }
  }

  if (Object.keys(overrides).length === 0) {
    return baseSchema;
  }

  return baseSchema.shape(overrides);
}

/**
 * Zorunlu olmayan field olusturur (base tipi koruyarak)
 */
function createNonRequiredField(baseType: string | undefined, alan: IFormAlanConfig): yup.AnySchema {
  let field: yup.AnySchema;

  switch (baseType) {
    case 'number':
      field = yup.number().nullable().optional().typeError('Gecerli bir sayi olmalidir') as any;
      break;
    case 'date':
      field = yup.date().nullable().optional() as any;
      break;
    case 'boolean':
      field = yup.boolean().optional() as any;
      break;
    default:
      field = yup.string().nullable().optional() as any;
      break;
  }

  return field;
}

/**
 * Zorunlu field olusturur (base tipi koruyarak)
 */
function createRequiredField(baseType: string | undefined, alan: IFormAlanConfig, hataMesaji: string): yup.AnySchema {
  let field: yup.AnySchema;

  switch (baseType) {
    case 'number':
      field = yup.number().nullable().required(hataMesaji).typeError('Gecerli bir sayi olmalidir') as any;
      break;
    case 'date':
      field = yup.date().nullable().required(hataMesaji) as any;
      break;
    case 'boolean':
      field = yup.boolean().required(hataMesaji) as any;
      break;
    default:
      field = yup.string().nullable().required(hataMesaji) as any;
      break;
  }

  // Regex pattern
  if (alan.regexPattern && typeof (field as any).matches === 'function') {
    try {
      const regex = new RegExp(alan.regexPattern);
      field = (field as any).matches(regex, alan.regexHataMesaji || 'Gecersiz format');
    } catch { /* gecersiz regex */ }
  }

  // Max uzunluk
  if (alan.maxUzunluk && typeof (field as any).max === 'function') {
    field = (field as any).max(alan.maxUzunluk);
  }

  return field;
}
